import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowRight, Check } from "lucide-react";
import { submitContact, formatApiError } from "@/lib/api";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { Overline, SectionShell } from "@/components/primitives";
import { CONTACT } from "@/data/content";

const GOALS = [
  "Executive visibility", "Company visibility", "Industry authority",
  "Recognition", "Market entry", "International visibility",
];

const EMPTY = {
  name: "", company: "", website: "", email: "", industry: "",
  geography: "", goals: [], challenge: "", context: "",
};

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleGoal = (g) =>
    setForm((f) => ({ ...f, goals: f.goals.includes(g) ? f.goals.filter((x) => x !== g) : [...f.goals, g] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and work email.");
      return;
    }
    const message = [form.challenge && `Challenge: ${form.challenge}`, form.context && `Context: ${form.context}`]
      .filter(Boolean).join("\n\n") || "Visibility mapping request.";
    setSubmitting(true);
    try {
      const data = await submitContact({
        name: form.name, email: form.email, organization: form.company,
        company: form.company, website: form.website, role: "",
        industry: form.industry, geography: form.geography, goals: form.goals,
        challenge: form.challenge, message,
      });
      setDone(true);
      setForm(EMPTY);
      toast.success(data.message || "Thank you. We will be in touch shortly.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const field = "w-full bg-transparent border-b hairline py-4 text-navy placeholder:text-navy/30 focus:outline-none focus:border-gold transition-colors duration-300 font-body";

  return (
    <div data-testid="contact-page">
      <section className="pt-40 md:pt-52 pb-16 border-b hairline">
        <SectionShell>
          <Reveal><Overline>Map Your Visibility</Overline></Reveal>
          <h1 className="mt-8 font-heading font-light text-navy tracking-tight text-[2.7rem] leading-[1.02] md:text-7xl lg:text-[5rem]">
            <MaskedLines lines={["Where should your", "company be visible?"]} delay={0.05} />
          </h1>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl text-lg md:text-xl leading-relaxed text-ink/70 font-body">
              A short strategic diagnostic. Tell us where you are and where you want to be — we'll map
              the ecosystems and opportunities that matter for your company and its leaders.
            </p>
          </Reveal>
        </SectionShell>
      </section>

      <SectionShell className="py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <div className="space-y-8">
              <Reveal delay={0.1}>
                <a href={`mailto:${CONTACT.email}`} className="flex items-start gap-4 group" data-testid="contact-email-link">
                  <Mail size={18} className="text-gold mt-1" />
                  <div><p className="overline text-navy/40 mb-1">Email</p><p className="text-navy group-hover:text-gold transition-colors">{CONTACT.email}</p></div>
                </a>
              </Reveal>
              <Reveal delay={0.15}>
                <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-start gap-4 group" data-testid="contact-phone-link">
                  <Phone size={18} className="text-gold mt-1" />
                  <div><p className="overline text-navy/40 mb-1">Phone &amp; WhatsApp</p><p className="text-navy group-hover:text-gold transition-colors">{CONTACT.phone}</p></div>
                </a>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="flex items-start gap-4">
                  <MapPin size={18} className="text-gold mt-1" />
                  <div><p className="overline text-navy/40 mb-1">Office</p>
                    <p className="text-navy leading-relaxed">{CONTACT.officeName}<br />{CONTACT.officeLines.map((l) => <span key={l}>{l}<br /></span>)}</p></div>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-7 lg:col-start-6">
            {done ? (
              <Reveal>
                <div className="border hairline p-10 md:p-14" data-testid="contact-success">
                  <p className="overline text-gold mb-4">Received</p>
                  <h2 className="font-heading font-light text-navy text-3xl md:text-4xl leading-snug">Thank you. We will be in touch shortly.</h2>
                  <button onClick={() => setDone(false)} className="mt-8 text-sm text-navy link-underline" data-testid="contact-send-another">Submit another</button>
                </div>
              </Reveal>
            ) : (
              <Reveal>
                <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <input className={field} placeholder="Name *" value={form.name} onChange={update("name")} data-testid="contact-input-name" />
                    <input className={field} placeholder="Company" value={form.company} onChange={update("company")} data-testid="contact-input-company" />
                    <input className={field} placeholder="Website" value={form.website} onChange={update("website")} data-testid="contact-input-website" />
                    <input className={field} type="email" placeholder="Work email *" value={form.email} onChange={update("email")} data-testid="contact-input-email" />
                    <input className={field} placeholder="Industry" value={form.industry} onChange={update("industry")} data-testid="contact-input-industry" />
                    <input className={field} placeholder="Geography / target market" value={form.geography} onChange={update("geography")} data-testid="contact-input-geography" />
                  </div>

                  <div>
                    <p className="overline text-navy/45 mb-4">What are you trying to build?</p>
                    <div className="flex flex-wrap gap-3" data-testid="contact-goals">
                      {GOALS.map((g) => {
                        const active = form.goals.includes(g);
                        return (
                          <button type="button" key={g} onClick={() => toggleGoal(g)} data-testid={`goal-${g.replace(/\s+/g, "-").toLowerCase()}`}
                            className={`inline-flex items-center gap-2 border px-4 py-2.5 text-sm transition-colors duration-300 ${active ? "border-navy bg-navy text-warm" : "border-navy/25 text-navy/70 hover:border-navy"}`}>
                            {active && <Check size={14} />}{g}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <textarea className={`${field} resize-none`} rows={2} placeholder="Biggest current visibility challenge" value={form.challenge} onChange={update("challenge")} data-testid="contact-input-challenge" />
                  <textarea className={`${field} resize-none`} rows={3} placeholder="Additional context" value={form.context} onChange={update("context")} data-testid="contact-input-message" />

                  <button type="submit" disabled={submitting} data-testid="contact-submit-button"
                    className="group inline-flex items-center gap-3 bg-navy text-warm px-9 py-4 text-sm tracking-wide hover:bg-navy/90 transition-colors duration-300 disabled:opacity-60">
                    {submitting ? "Sending…" : "Map Your Visibility"}
                    <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </SectionShell>
    </div>
  );
}
